import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { imod } from '../../utils/math.js';
import { USE_POINTER_LOCK } from '../../utils/pointer-lock.js';
import { ZERO, dpiSnap, fromClientXY } from '../../utils/vector.js';
import { useInfiniteDrag } from '../use-infinite-drag.js';
import { cursor, scrubbing } from './use-scrub.css.js';

/**
 * An implementation detail of the `useScrub` hook. Implements the pointer portion of the scrubbing
 * behavior (mouse / touch / pen). Also handles rendering the "fake cursor" while the native cursor
 * is hidden by `requestPointerLock`.
 */
function usePointerScrub(ctx) {
    const scrubEl = useRef(undefined);
    const start = useRef(ZERO);
    const delta = useRef(ZERO);
    const cursorRef = useRef(null);
    const [pointerLockPermission, setPointerLockPermission] = useState(1 /* PointerLockPermission.PENDING */);
    const [scrubbing$1, getInfiniteDragProps] = useInfiniteDrag({
        customButtonCheck: true,
        disabled: ctx.disabled,
        onBeforeDrag(e) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            const button = e.button;
            // Scrubbing can start with left or middle click. Middle click should typically allow
            // scrubbing the element from anywhere.
            if (button !== 0 /* MouseButton.LEFT */ && button !== 1 /* MouseButton.MIDDLE */)
                return false;
            const source = e.altKey
                ? 'alt-pointer'
                : button === 1 /* MouseButton.MIDDLE */
                    ? 'middle'
                    : 'pointer';
            const event = { source, target: e.target };
            return ctx.onBeforeScrub?.(event) ?? true;
        },
        onDragStart(e) {
            const target = e.currentTarget;
            document.documentElement.classList.add(scrubbing);
            scrubEl.current = target;
            start.current = dpiSnap(fromClientXY(e));
            ctx.onScrubStart?.({
                target,
                delta: ZERO,
                movement: ZERO,
                shiftKey: e.shiftKey,
                source: 'pointer',
            });
        },
        onDrag(e) {
            ctx.onScrub?.({
                target: e.currentTarget,
                delta: e.delta,
                movement: e.movement,
                shiftKey: e.shiftKey,
                source: 'pointer',
            });
            delta.current = e.delta;
            updatePosition(cursorRef.current, start.current, delta.current);
        },
        onDragEnd(e) {
            document.documentElement.classList.remove(scrubbing);
            start.current = ZERO;
            delta.current = ZERO;
            const target = scrubEl.current;
            scrubEl.current = undefined;
            ctx.onScrubEnd?.(e.cancelled
                ? {
                    target,
                    delta: ZERO,
                    movement: ZERO,
                    shiftKey: false,
                    source: 'pointer',
                    cancelled: true,
                }
                : {
                    target: e.currentTarget,
                    delta: e.delta,
                    movement: e.movement,
                    shiftKey: e.shiftKey,
                    source: 'pointer',
                    cancelled: false,
                });
        },
        onPointerLockPermissionChange: setPointerLockPermission,
    });
    const cursorCb = useCallback((el) => {
        cursorRef.current = el;
        updatePosition(el, start.current, delta.current);
    }, []);
    const Cursor = useCallback((props) => {
        if (!USE_POINTER_LOCK ||
            !scrubbing$1 ||
            pointerLockPermission === 0 /* PointerLockPermission.DENIED */) {
            return null;
        }
        return createPortal(jsxs("div", { "data-fpl-component": "", ref: cursorCb, className: cursor, children: [jsx("div", { children: props.children }), jsx("div", { children: props.children }), jsx("div", { children: props.children }), jsx("div", { children: props.children })] }), document.body);
    }, 
    // cursorCb is a stable function because it has no dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scrubbing$1, pointerLockPermission]);
    return [
        scrubbing$1,
        (...props) => getInfiniteDragProps({ className: scrubbing$1 ? scrubbing : undefined }, ...props),
        Cursor,
    ];
}
function updatePosition(el, start, delta) {
    if (!el)
        return;
    const x = imod(start.x + delta.x, window.innerWidth);
    const y = imod(start.y + delta.y, window.innerHeight);
    // the "cursor" is really a giant plane with 4 cursors, one on each corner. We place a different
    // corner at the mouse position depending on which quadrant the mouse is in, so that we get the
    // illusion that the cursor wraps around the edges of the screen.
    // ┌─────────────┬──────────────┐
    // │             │              │
    // │     0, 0    │   -100%, 0   │
    // │             │              │
    // ├─────────────┼──────────────┤
    // │             │              │
    // │   0, -100%  │ -100%, -100% │
    // │             │              │
    // └─────────────┴──────────────┘
    const px = x > window.innerWidth / 2 ? -100 : 0;
    const py = y > window.innerHeight / 2 ? -100 : 0;
    el.style.transform = `translate(calc(${px}% + ${x}px), calc(${py}% + ${y}px))`;
}

export { usePointerScrub };
//# sourceMappingURL=use-scrub-pointer.js.map
